import { Receipt, History, Eye, Mail } from 'lucide-react';
import { toast } from 'react-toastify';

import Breadcrumb from '../../../components/Breadcrumb/Breadcrumb';
import Tabs from '../../../components/Tabs/Tabs';
import Table, { StatusPill } from '../../../components/Table/Table';

import usePayrollEntity from '../../../utils/usePayrollEntity';
import { fmtMoney } from '../../../utils/payrollFormat';
import { toIsoDate } from '../../../utils/dateFormat';

const ICON = { size: 15 };

const TABLE_PROPS = {
  search: true,
  searchLabel: 'Search',
  pageSize: 10,
  scrollClassName: '',
};

/* Quiet row action, same as the other payroll pages. */
const ROW_BTN =
  'inline-flex items-center gap-1.5 h-7 px-2 rounded-md text-[12px] font-semibold transition-colors';

/* ── Generate & Email Payslips ───────────────────────────────────────────── */
const GeneratePayslipsTab = () => {
  const { list, loading, save } = usePayrollEntity('payslips');

  const notify = (r) => toast.info(`Previewing payslip for ${r.name} (demo).`, { autoClose: 2000 });

  const sendEmail = (r) =>
    save({ id: r.id, emailStatus: 'Sent' }, true).then(() =>
      toast.success(`Payslip emailed to ${r.name}.`, { autoClose: 2000 })
    );

  return (
    <Table
      {...TABLE_PROPS}
      title="Generate & Email Payslips"
      loading={loading}
      rows={list}
      rowKey="id"
      searchKeys={['empNo', 'name']}
      searchPlaceholder="Search payslips..."
      emptyMessage="No payslips found"
      columns={[
        {
          key: 'name',
          label: 'Employee',
          grow: true,
          className: 'font-semibold text-gray-900',
          render: (r) => `${r.name} (${r.empNo})`,
        },
        { key: 'period', label: 'Period', className: 'text-gray-600' },
        {
          key: 'net',
          label: 'Net Pay',
          align: 'right',
          className: 'font-semibold text-gray-900',
          render: (r) => fmtMoney(r.net),
        },
        {
          key: 'emailStatus',
          label: 'Email Status',
          align: 'center',
          render: (r) => <StatusPill label={r.emailStatus} />,
        },
        {
          key: 'actions',
          label: 'Actions',
          align: 'center',
          sortable: false,
          render: (r) => (
            <div className="flex items-center justify-center gap-1">
              <button
                type="button"
                onClick={() => notify(r)}
                className={`${ROW_BTN} text-gray-600 hover:bg-gray-100`}
              >
                <Eye size={14} />
                View
              </button>
              <button
                type="button"
                onClick={() => sendEmail(r)}
                className={`${ROW_BTN} text-primary-700 hover:bg-primary-50`}
              >
                <Mail size={14} />
                Email
              </button>
            </div>
          ),
        },
      ]}
    />
  );
};

/* ── Employee Payslip History ────────────────────────────────────────────── */
const PayslipHistoryTab = () => {
  const { list, loading } = usePayrollEntity('payslipHistory');

  return (
    <Table
      {...TABLE_PROPS}
      title="Employee Payslip History"
      loading={loading}
      rows={list}
      rowKey="id"
      searchKeys={['empNo', 'name', 'period']}
      searchPlaceholder="Search payslip history..."
      emptyMessage="No payslip history found"
      columns={[
        {
          key: 'name',
          label: 'Employee',
          grow: true,
          className: 'font-semibold text-gray-900',
          render: (r) => `${r.name} (${r.empNo})`,
        },
        { key: 'period', label: 'Period', className: 'text-gray-600' },
        {
          key: 'net',
          label: 'Net Pay',
          align: 'right',
          className: 'font-semibold text-gray-900',
          render: (r) => fmtMoney(r.net),
        },
        {
          key: 'downloadedOn',
          label: 'Last Downloaded',
          align: 'center',
          className: 'text-gray-600 tabular-nums',
          render: (r) => toIsoDate(r.downloadedOn),
        },
      ]}
    />
  );
};

/* ── Page ────────────────────────────────────────────────────────────────── */
const Payslips = () => {
  const tabs = [
    { key: 'generate', label: 'Generate & Email Payslips', icon: <Receipt {...ICON} />, content: <GeneratePayslipsTab /> },
    { key: 'history', label: 'Employee Payslip History', icon: <History {...ICON} />, content: <PayslipHistoryTab /> },
  ];

  return (
    <div className="p-6 space-y-4">
      <Breadcrumb
        trail={['Payroll', 'Payslips']}
        title="Payslips"
        subtitle="Generate, preview, email and review the history of employee payslips."
      />
      <Tabs tabs={tabs} defaultTab="generate" />
    </div>
  );
};

export default Payslips;